<template>
  <div v-if="!consentGiven" class="cookie-consent-banner">
    <div class="banner-content">
      <div class="banner-icon">
        <v-icon color="primary" size="24">mdi-cookie</v-icon>
      </div>
      <div class="banner-text">
        <h3>Cookie-Hinweis</h3>
        <p>
          Wir verwenden nur essentielle Cookies, um die Funktionalität unserer Website sicherzustellen.
        </p>
      </div>
      <div class="banner-actions">
        <v-btn 
          variant="text" 
          color="primary" 
          class="details-btn"
          @click="openDetails"
        >
          <span>Mehr erfahren</span>
        </v-btn>
        <v-btn 
          color="primary" 
          variant="elevated"
          class="accept-btn"
          @click="giveConsent"
        >
          <v-icon start size="small">mdi-check</v-icon>
          <span>Akzeptieren</span>
        </v-btn>
      </div>
    </div>

    <!-- v-dialog zur Anzeige der Datenschutzerklärung -->
    <v-dialog v-model="showDetails" max-width="800" class="privacy-dialog">
      <v-card class="privacy-card">
        <v-card-title class="dialog-header">
          <v-icon color="primary" size="24" class="mr-2">mdi-shield-lock</v-icon>
          <span>Datenschutzerklärung</span>
        </v-card-title>
        <v-divider class="card-divider"></v-divider>
        <v-card-text class="dialog-content">
          <PrivacyPolicy />
        </v-card-text>
        <v-divider class="card-divider"></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            variant="elevated"
            class="close-btn"
            @click="closeDetails"
          >
            <span>Schließen</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import PrivacyPolicy from '@/views/DatenschutzerklaerungView.vue';

const consentGiven = ref(false);
const showDetails = ref(false);

onMounted(() => {
  consentGiven.value = localStorage.getItem('cookieConsent') === 'true';
});

const giveConsent = () => {
  consentGiven.value = true;
  localStorage.setItem('cookieConsent', 'true');
};

const openDetails = () => {
  showDetails.value = true;
};

const closeDetails = () => {
  showDetails.value = false;
};
</script>

<style scoped>
.cookie-consent-banner {
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  max-width: 1200px;
  margin: 0 auto;
  background: rgba(25, 25, 30, 0.85);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: rgba(255, 255, 255, 0.9);
  padding: 0;
  z-index: 1000;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.banner-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px 20px;
  gap: 20px;
  background: linear-gradient(
    90deg,
    rgba(33, 150, 243, 0.05),
    transparent
  );
}

.banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(33, 150, 243, 0.1);
  flex-shrink: 0;
}

.banner-text {
  flex: 1;
}

.banner-text h3 {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 6px 0;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.banner-text p {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.banner-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.details-btn {
  opacity: 0.85;
  transition: all 0.2s ease;
  text-decoration: underline rgba(255, 255, 255, 0.2);
}

.details-btn:hover {
  opacity: 1;
  text-decoration: underline rgba(33, 150, 243, 0.6);
}

.accept-btn {
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2) !important;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.accept-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(33, 150, 243, 0.3) !important;
}

/* Dialog-Styling */
.privacy-dialog :deep(.v-overlay__content) {
  border-radius: 16px;
  overflow: hidden;
}

.privacy-card {
  background: rgba(25, 25, 30, 0.95) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.dialog-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(
    90deg,
    rgba(33, 150, 243, 0.2),
    rgba(33, 150, 243, 0.1),
    transparent
  );
  font-size: 18px;
  font-weight: 500;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

.dialog-content {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.dialog-actions {
  padding: 12px 20px;
}

.close-btn {
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2) !important;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(33, 150, 243, 0.3) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .cookie-consent-banner {
    left: 10px;
    right: 10px;
    bottom: 10px;
  }
  
  .banner-content {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    gap: 15px;
  }
  
  .banner-text {
    padding: 0;
  }
  
  .banner-actions {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 480px) {
  .banner-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .details-btn,
  .accept-btn {
    width: 100%;
  }
}
</style>